import { Component, Input, OnInit } from '@angular/core';
import { User } from '../../../shared/services/users/users.service';
import { DocumentService, DocumentQueryParams } from '../../../shared/services/document/document.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { GetFilesForUserApiResponse } from '../../../shared/models/api.model';
import { DocumentTypesService } from 'src/app/modules/shared/services/document-types/document-types.service';

@Component({
  selector: 'app-user-detail-files',
  templateUrl: './user-detail-files.component.html',
  styleUrls: ['./user-detail-files.component.scss']
})
export class UserDetailFilesComponent implements OnInit {
  @Input() user!: User;

  // data
  file_data: GetFilesForUserApiResponse = { documents: [] };
  isLoading = true;
  search_query: string = '';
  search_is_active = false;
  // file sidebar state
  selectedFileId: string | null = null;
  isSidebarOpen = false;

  // query
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  filter_fileType: number[] | undefined = undefined;
  filter_extension: string[] = [];
  show_filters = false;

  constructor(
    private documentService: DocumentService,
    private documentTypesService: DocumentTypesService,
    private Toaster: ToasterService
  ) { }

  ngOnInit(): void {
    if (!this.user || !this.user.USER_ID) {
      this.isLoading = false;
      this.Toaster.warning('User not loaded; cannot fetch files.', 4000);
      return;
    }
    this.getFiles();
  }

  public toggleFilters() {
    this.show_filters = !this.show_filters;
  }

  public async getFiles() {
    this.isLoading = true;
    this.search_is_active = !!this.search_query;
    try {
      const params: Partial<DocumentQueryParams> = {
        page: this.currentPage,
        pageSize: this.pageSize,
        search: this.search_query,
        fileType: this.filter_fileType,
        extension: this.filter_extension
      };
      console.log(`Getting files for user ${this.user.USER_ID} with params:`, params);
      const response = await this.documentService.listFilesByUser(this.user.USER_ID, params);
      this.file_data = response;
      // Handle pagination details from API response
      const anyRes: any = response as any;
      // Sync page and pageSize from server if present
      if (typeof anyRes?.page === 'number') {
        this.currentPage = anyRes.page;
      }
      if (typeof anyRes?.pageSize === 'number') {
        this.pageSize = anyRes.pageSize;
      }
      this.totalItems = anyRes?.total ?? 0;
      const respPageSize = anyRes?.pageSize ?? this.pageSize;
      this.totalPages = respPageSize ? Math.ceil(this.totalItems / respPageSize) : 0;
    } catch (error: any) {
      this.Toaster.warning(`Error fetching files: ${error?.message ?? error}`, 4000);
      console.error('Error fetching files:', error);
    } finally {
      this.isLoading = false;
    }
  }

  public refreshFileList() {
    this.getFiles();
  }

  public clearSearch() {
    this.search_query = '';
    this.onSearch();
  }

  public onSearch() {
    this.currentPage = 1;
    this.getFiles();
  }

  public onPageChange(page: number) {
    this.currentPage = page;
    this.getFiles();
  }

  // Open file details sidebar (reuse folders view sidebar)
  public openFileSidebar(fileId: string) {
    this.selectedFileId = fileId;
    this.isSidebarOpen = true;
  }

  public closeSidebar() {
    this.isSidebarOpen = false;
  }

  // Pagination helpers and handlers (match ApplicationUsersComponent)
  public get showingFrom(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  public get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  public goToPage(page: number): void {
    if (page < 1) return;
    if (this.totalPages && page > this.totalPages) return;
    this.currentPage = page;
    this.getFiles();
  }

  public nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  public prevPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  public firstPage(): void {
    this.goToPage(1);
  }

  public lastPage(): void {
    if (this.totalPages) {
      this.goToPage(this.totalPages);
    }
  }

  public onPageSizeChange(size: number): void {
    if (!size || size < 1) return;
    this.pageSize = size;
    this.currentPage = 1;
    this.getFiles();
  }

  /** Returns the sum total of all selected filters */
  public sumOfFilterLength(): number {
    return (this.filter_fileType?.length ?? 0) + (this.filter_extension?.length ?? 0);
  }

  public onFilterTypeChange(type_id: number) {
    if (this.filter_fileType?.includes(type_id)) {
      this.filter_fileType = this.filter_fileType.filter((type) => type !== type_id);
    } else {
      if (!this.filter_fileType) {
        this.filter_fileType = [];
      }
      this.filter_fileType.push(type_id);
    }
    this.currentPage = 1;
    this.getFiles();
  }

  public onFilterTypeClear() {
    this.filter_fileType = undefined;
    this.currentPage = 1;
    this.getFiles();
  }
}
